import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray, ReactiveFormsModule, FormsModule } from '@angular/forms';

import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { Guid } from 'guid-typescript';
import { FileUploadComponent } from '../../../../common/components/file/file.component';
import { CostType, FundingSourceType } from '../../../../common/models/common';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { FormattedNumberInputDirective } from '../../../../common/directives/inputs/formatted-number.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BudgetPlanStatusComponent } from './status/status.component';
import { BudgetItem, BudgetPlan, FundingSource } from '../../../../common/models/submission';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-budget-plan',
    templateUrl: './budget.component.html',
    styleUrls: ['./budget.component.scss'],
    imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatExpansionModule,
    MatTooltipModule,
    FileUploadComponent,
    BudgetPlanStatusComponent,
    FormattedNumberInputDirective
]
})
export class BudgetPlanComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() budgetPlanForm?: FormGroup;

  // QueryLists for direct cost panels and their textareas
  @ViewChildren('directPanel', { read: MatExpansionPanel }) directExpansionPanels!: QueryList<MatExpansionPanel>;
  @ViewChildren('directDescriptionInputs') directDescriptionInputs!: QueryList<ElementRef<HTMLTextAreaElement>>;

  // QueryLists for indirect cost panels and their textareas
  @ViewChildren('indirectPanel', { read: MatExpansionPanel }) indirectExpansionPanels!: QueryList<MatExpansionPanel>;
  @ViewChildren('indirectDescriptionInputs') indirectDescriptionInputs!: QueryList<ElementRef<HTMLTextAreaElement>>;

  // QueryLists for funding panels and their amount input
  @ViewChildren('fundingPanel', { read: MatExpansionPanel }) fundingExpansionPanels!: QueryList<MatExpansionPanel>;
  @ViewChildren('fundingAmountInputs') fundingAmountInputs!: QueryList<ElementRef<HTMLInputElement>>;
  
  budgetType = CostType;
  fundingSourceType = FundingSourceType;
  private fundingSourceTypes: { type: FundingSourceType; label: string }[] = [
    { type: FundingSourceType.OwnFunds, label: 'I. Vlastiti izvori' },
    { type: FundingSourceType.OtherPublicAuthorities, label: 'II. Ostala tijela javne vlasti' },
    { type: FundingSourceType.LocalAndRegionalAuthorities, label: 'III. Jedinice lokalne i područne (regionalne) samouprave' },
    { type: FundingSourceType.Other, label: 'IV. Drugo' }
  ];
  
  getFundingSourceLabel(type: FundingSourceType): string {
    return this.fundingSourceTypes.find(source => source.type === type)?.label || 'Nepoznato';
  }
  
  get directCosts(): FormArray {
    return this.budgetPlanForm?.get('directCosts') as FormArray;
  }

  get indirectCosts(): FormArray {
    return this.budgetPlanForm?.get('indirectCosts') as FormArray;
  }

  get fundingSources(): FormArray {
    return this.budgetPlanForm?.get('fundingSources') as FormArray;
  }

  get totalDirectCostsRequested(): number {
    return this.directCosts?.controls.reduce((sum, control) => {
      return sum + (control.get('amountRequested')?.value || 0);
    }, 0) || 0;
  }
  
  get totalDirectCostsActual(): number {
    return this.directCosts?.controls.reduce((sum, control) => {
      return sum + (control.get('totalAmount')?.value || 0);
    }, 0) || 0;
  }
  
  get totalIndirectCostsRequested(): number {
    return this.indirectCosts?.controls.reduce((sum, control) => {
      return sum + (control.get('amountRequested')?.value || 0);
    }, 0) || 0;
  }
  
  get totalIndirectCostsActual(): number {
    return this.indirectCosts?.controls.reduce((sum, control) => {
      return sum + (control.get('totalAmount')?.value || 0);
    }, 0) || 0;
  }
  
  
  get totalCostsRequested(): number {
    return this.totalDirectCostsRequested + this.totalIndirectCostsRequested;
  }
  
  get totalCostsActual(): number {
    return this.totalDirectCostsActual + this.totalIndirectCostsActual;
  }
  
  get totalFundingFromOtherSources(): number {
    return this.fundingSources.controls.reduce((sum, control) => {
      return sum + (control.get('amount')?.value || 0);
    }, 0);
  }
  
  get totalFunding(): number {
    return this.totalCostsRequested + this.totalFundingFromOtherSources;
  }


  constructor(private fb: FormBuilder, private snackBar: MatSnackBar) { 
  }

  ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
  }

  addBudgetItem(type: CostType, item?: BudgetItem, autoFocus: boolean = true): void {
    const budgetItemForm = this.fb.group({
      id: [item?.id || ''],
      type: [type, Validators.required],
      description: [item?.description || '', [Validators.required, Validators.maxLength(500)]],
      amountRequested: [item?.amountRequested || 0, [Validators.required, Validators.min(0)]],
      totalAmount: [item?.totalAmount || 0, [Validators.required, Validators.min(0)]],
      explanation: [item?.explanation || '', [Validators.required, Validators.maxLength(1000)]],
      fileStorageLocation: [item?.fileStorageLocation || `budgetitems/${Guid.create().toString()}`]
    });
  
    if (type === CostType.Direct) {
      this.directCosts.push(budgetItemForm);
    } else {
      this.indirectCosts.push(budgetItemForm);
    }

    // Automatically expand and focus the new item
    setTimeout(() => {
      if (!autoFocus) return;

      if (type === CostType.Direct && this.directExpansionPanels.length > 0) {
        const panel = this.directExpansionPanels.last;
        panel.open();
        setTimeout(() => {
          const lastInput = this.directDescriptionInputs.last?.nativeElement;
          lastInput?.focus();
        }, 50);
      } else if (type === CostType.Indirect && this.indirectExpansionPanels.length > 0) {
        const panel = this.indirectExpansionPanels.last;
        panel.open();
        setTimeout(() => {
          const lastInput = this.indirectDescriptionInputs.last?.nativeElement;
          lastInput?.focus();
        }, 50);
      }
    }, 50);
  }

  removeBudgetItem(type: CostType, index: number): void {
    if (type === CostType.Direct) {
      this.directCosts.removeAt(index);
    } else {
      this.indirectCosts.removeAt(index);
    }
    this.snackBar.open('Stavka troška uklonjena', 'Zatvori', {  
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top' });
  }


  addFundingSource(sourceType: FundingSourceType, source?: FundingSource): void {
    const fundingSourceForm = this.fb.group({
      id: [source?.id || ''],
      type: [sourceType, Validators.required],
      amount: [source?.amount || 0, [Validators.required, Validators.min(0)]]
    });
    this.fundingSources.push(fundingSourceForm);
  }

  updateFundingSource(type: FundingSourceType): void {
    const existingControl = this.fundingSources.controls.find(control => control.get('type')!.value === type);
  
    if (existingControl) {
      existingControl.patchValue({ type });
    } 
  }

  removeFundingSource(index: number): void {
    this.fundingSources.removeAt(index);
    this.snackBar.open('Izvor financiranja uklonjen', 'Zatvori', {  
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top' });
  }

  closePanel(panel: MatExpansionPanel): void {
    panel.close();
  }
}